/**
 * "Move…": the non-drag way to make every move a drag can make. A disclosure
 * button and a menu of the acts on offer for this card -- each one OPENS the
 * governed act, exactly as dropping on its column would. Nothing here writes.
 *
 * Keyboard: Enter / Space / ArrowDown open it on the first item; ArrowUp /
 * ArrowDown / Home / End move; Enter chooses; Escape or Tab closes and focus
 * returns to the button.
 */
import React, { useEffect, useId, useRef, useState } from 'react';
import type { OfferedMove } from './transitions';

export interface MoveMenuProps {
  /** Whose menu this is, for the button's accessible name ("Move Ann Nelson"). */
  name: string;
  moves: OfferedMove[];
  /** Column labels by key, for "to Submitted to DCF". */
  columnLabel: (key: string) => string;
  onChoose: (move: OfferedMove) => void;
}

export default function MoveMenu({ name, moves, columnLabel, onChoose }: MoveMenuProps) {
  const [open, setOpen] = useState(false);
  const [at, setAt] = useState(0);
  const btn = useRef<HTMLButtonElement | null>(null);
  const items = useRef<Array<HTMLButtonElement | null>>([]);
  const menuId = useId();

  useEffect(() => { if (open) items.current[at]?.focus(); }, [open, at]);
  useEffect(() => {
    if (!open) return undefined;
    const away = (e: MouseEvent) => {
      const t = e.target as Node;
      if (btn.current && btn.current.parentElement && !btn.current.parentElement.contains(t)) setOpen(false);
    };
    document.addEventListener('mousedown', away);
    return () => document.removeEventListener('mousedown', away);
  }, [open]);

  if (moves.length === 0) return null;
  const close = (refocus = true) => { setOpen(false); if (refocus) btn.current?.focus(); };
  const show = (i: number) => { setAt(i); setOpen(true); };

  return (
    <span className="okb-move" onClick={(e) => e.stopPropagation()} onPointerDown={(e) => e.stopPropagation()}>
      <button
        type="button"
        ref={btn}
        className="okb-move__btn"
        aria-haspopup="menu"
        aria-expanded={open}
        aria-controls={open ? menuId : undefined}
        aria-label={`Move ${name}`}
        onClick={() => (open ? close() : show(0))}
        onKeyDown={(e) => {
          if (e.key === 'ArrowDown') { e.preventDefault(); show(0); }
          if (e.key === 'ArrowUp') { e.preventDefault(); show(moves.length - 1); }
        }}
      >
        Move…
      </button>
      {open && (
        <div id={menuId} role="menu" aria-label={`Move ${name} to`} className="okb-move__menu"
          onKeyDown={(e) => {
            if (e.key === 'Escape') { e.preventDefault(); e.stopPropagation(); close(); }
            else if (e.key === 'Tab') close(false);
            else if (e.key === 'ArrowDown') { e.preventDefault(); setAt((i) => (i + 1) % moves.length); }
            else if (e.key === 'ArrowUp') { e.preventDefault(); setAt((i) => (i - 1 + moves.length) % moves.length); }
            else if (e.key === 'Home') { e.preventDefault(); setAt(0); }
            else if (e.key === 'End') { e.preventDefault(); setAt(moves.length - 1); }
          }}>
          {moves.map((m, i) => (
            <button
              // Two in-place acts share `to` (the card's own column): the act makes the key unique.
              key={`${m.to}:${m.act}`}
              type="button"
              role="menuitem"
              tabIndex={i === at ? 0 : -1}
              ref={(el) => { items.current[i] = el; }}
              className="okb-move__item"
              data-to={m.to}
              onClick={() => {
                setOpen(false);
                onChoose(m);
                // The item is gone: when nothing else took focus (the act opened nothing focusable, or was refused),
                // it comes back to this card's Move button rather than falling to the page body.
                window.setTimeout(() => {
                  const a = document.activeElement;
                  if ((!a || a === document.body) && btn.current && btn.current.isConnected) btn.current.focus();
                }, 0);
              }}
            >
              <span className="okb-move__label">{m.label}</span>
              <span className="okb-move__to">{m.inPlace ? m.opens : `to ${columnLabel(m.to)} · ${m.opens}`}</span>
            </button>
          ))}
        </div>
      )}
    </span>
  );
}
